import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { addPosStock, posReserveUnits, describePosUnits } from '../utils/posUtil';
import { spinnerRing } from '../utils/bookingUtils';
import { describeWriteError } from '../utils/firebaseErrorUtil';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import '../css/Pos.css';

/**
 * Records a delivery against one product.
 *
 * ASKS FOR THE DELTA, NOT THE NEW TOTAL. "12 arrived" is something the person
 * holding the delivery note knows; "there are now 31" is a sum that depends on
 * nothing having sold while they counted. The running total is shown as they
 * type so the result is still visible before they commit to it.
 *
 * COMMITS ON ITS OWN, not with the product form around it. Stock arriving is
 * an event with a time and a person attached, so it gets its own audit entry
 * and takes effect immediately — rather than riding along with a price change
 * and landing on the trail as one indistinguishable "product updated".
 *
 * @param {object} props
 * @param {object} props.product - the product being restocked; needs `id`,
 *   `name` and `supplies`.
 * @param {(newTotal: number) => void} props.onAdded - the stock level after
 *   the addition, so the form behind can show it without re-reading.
 * @param {() => void} props.onClose
 * @param {boolean} [props.fromReserve] - this client keeps a reserve, so by
 *   default what is added to the shelf is TAKEN from it, and no more than it
 *   holds. A "Take from reserve" box (ticked by default) lets a delivery that
 *   never went into the storeroom go straight onto the shelf instead.
 * @returns {JSX.Element}
 */
function PosAddStockModal({ product, onAdded, onClose, fromReserve = false }) {
  const { clientId } = useConfig();
  const { staffEmail } = usePosSession();
  const [quantity, setQuantity] = useState('');
  const [takeFromReserve, setTakeFromReserve] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The box only exists for clients with a reserve; for everyone else this is
  // a plain delivery, exactly as before.
  const useReserve = fromReserve && takeFromReserve;

  const current = Math.max(0, Math.floor(Number(product?.supplies) || 0));
  const parsed = Number(quantity);
  const inReserve = posReserveUnits(product);
  // Blocked, not clamped: silently adding fewer than typed would leave the
  // shelf and the delivery note disagreeing with nobody told.
  const overReserve = useReserve && Number.isInteger(parsed) && parsed > inReserve;
  const valid = Number.isInteger(parsed) && parsed >= 1 && !overReserve;

  const handleAdd = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      const total = await addPosStock(clientId, product, quantity, staffEmail, {
        fromReserve: useReserve,
        skippedReserve: fromReserve && !takeFromReserve,
      });
      onAdded(total);
      onClose();
    } catch (err) {
      setError(describeWriteError(err, { what: 'the stock change', path: `posTenants/${clientId}/products` }));
      setSaving(false);
    }
  };

  return createPortal(
    // Same rules as every other dialog here: no backdrop click-to-close
    // (CLAUDE.md > Behavior), a × in the header, Cancel in the footer. Stacked
    // above the product form it opens from — see .pos-modal-overlay-stacked.
    <div className="pos-modal-overlay pos-modal-overlay-stacked">
      <div className="pos-modal pos-modal-narrow" role="dialog" aria-modal="true" aria-label={`Add supplies to ${product?.name || 'product'}`}>
        <header className="pos-modal-head">
          <h3>Add supplies</h3>
          <button type="button" className="pos-modal-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pos-modal-body">
          <p className="pos-stock-product">{product?.name}</p>

          <label className="pos-field">
            <span>How many are being added?</span>
            <input
              type="number"
              min="1"
              max={useReserve ? inReserve : undefined}
              step="1"
              value={quantity}
              onChange={(e) => { setQuantity(e.target.value); setError(''); }}
              // Enter is the natural way out of a one-field dialog, and this
              // one is used with a delivery note in the other hand.
              onKeyDown={(e) => { if (e.key === 'Enter' && valid) { e.preventDefault(); handleAdd(); } }}
              disabled={saving}
              /* eslint-disable-next-line jsx-a11y/no-autofocus */
              autoFocus
            />
          </label>

          {/* The arithmetic done for them, so nobody has to decide whether the
              field wanted the delta or the total. */}
          <p className="pos-stock-sum">
            {valid
              ? <>In stock: <strong>{current}</strong> &rarr; <strong>{current + parsed}</strong></>
              : <>In stock: <strong>{current}</strong></>}
          </p>

          {fromReserve && (
            <label className="pos-check">
              <input
                type="checkbox"
                checked={takeFromReserve}
                onChange={(e) => { setTakeFromReserve(e.target.checked); setError(''); }}
                disabled={saving}
              />
              <span>
                <strong>Take from reserve</strong>
                <em>
                  {takeFromReserve
                    ? 'Moves these units out of the reserve and onto the shelf.'
                    : 'Adds these units straight onto the shelf. The reserve is left as it is.'}
                </em>
              </span>
            </label>
          )}

          {useReserve && (
            <p className={`pos-stock-sum${overReserve ? ' ps-short' : ''}`}>
              {overReserve
                ? <>Only <strong>{describePosUnits(inReserve, product)}</strong> left in reserve — you can add up to {inReserve}.</>
                : <>Taken from reserve: <strong>{inReserve}</strong> &rarr; <strong>{valid ? inReserve - parsed : inReserve}</strong></>}
            </p>
          )}

          <p className="pos-stock-note">
            {useReserve
              ? 'Moved out of the reserve and onto the shelf in one step. '
              : ''}
            Applied to whatever is on the shelf when this is saved, not to the number
            above — anything sold in the meantime still counts.
          </p>

          {error && <p className="pos-error">{error}</p>}
          {saving && <div className="pos-modal-saving">{spinnerRing('Adding…')}</div>}
        </div>

        <footer className="pos-modal-foot">
          <span className="pos-foot-spacer" />
          <button type="button" className="pos-btn pos-btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="pos-btn pos-btn-primary" onClick={handleAdd} disabled={saving || !valid}>
            {saving ? 'Adding…' : 'Add supplies'}
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

export default PosAddStockModal;
